import React, { useState, useEffect } from "react";
import axios from "axios";
import { api_urls } from "../../../utils/api-urls";

const NameVerification = () => {
  const [restrictedNames, setRestrictedNames] = useState([]);
  const [newName, setNewName] = useState("");
  const [reason, setReason] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("name");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  useEffect(() => { fetchRestrictedNames(); }, []);

  const fetchRestrictedNames = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(api_urls + "api/admin/get-restricted-names");
      if (data.success) setRestrictedNames(data.data || []);
    } catch (error) {
      showMessage("Failed to fetch restricted names", "error");
    } finally { setLoading(false); }
  };

  const handleAddName = async (e) => {
    e.preventDefault();
    if (!newName.trim()) { showMessage("Please enter a name", "error"); return; }
    try {
      setLoading(true);
      const { data } = await axios.post(api_urls + "api/admin/add-restricted-name", { name: newName.trim(), reason: reason.trim() || "Restricted by admin" });
      if (data.success) { showMessage("Name added successfully. " + (data.matchedUsers || 0) + " users matched (exact firstName).", "success"); setNewName(""); setReason(""); fetchRestrictedNames(); }
      else showMessage(data.message || "Failed to add name", "error");
    } catch (error) { showMessage(error.response?.data?.message || "Error adding name", "error"); }
    finally { setLoading(false); }
  };

  const handleDeleteName = async (id) => {
    if (!window.confirm("Are you sure you want to remove this restricted name?")) return;
    try {
      const { data } = await axios.delete(api_urls + "api/admin/remove-restricted-name/" + id);
      if (data.success) { showMessage("Name removed successfully", "success"); fetchRestrictedNames(); }
    } catch (error) { showMessage("Error removing name", "error"); }
  };

  const handleSearchUsers = async () => {
    if (!searchQuery.trim()) return;
    try {
      setLoading(true);
      let url = api_urls + "api/admin/search-users-by-name?";
      if (searchType === "phone") {
        url += "phone=" + encodeURIComponent(searchQuery);
      } else {
        url += "name=" + encodeURIComponent(searchQuery);
      }
      const { data } = await axios.get(url);
      if (data.success) { setSearchResults(data.data || []); if (data.data?.length === 0) showMessage("No users found", "info"); }
    } catch (error) { showMessage("Error searching users", "error"); }
    finally { setLoading(false); }
  };

  const showMessage = (text, type) => { setMessage({ text, type }); setTimeout(() => setMessage({ text: "", type: "" }), 4000); };

  const handleToggleVerification = async (userId, newStatus, idx) => {
    try {
      setLoading(true);
      const { data } = await axios.post(api_urls + "api/admin/toggle-user-verified", { userId, isVerified: newStatus });
      if (data.success) {
        showMessage("User " + (newStatus ? "verified" : "unverified") + " successfully", "success");
        const updated = [...searchResults];
        updated[idx] = { ...updated[idx], isVerified: newStatus, verificationNote: newStatus ? "Verified by admin" : "Unverified by admin" };
        setSearchResults(updated);
      } else { showMessage(data.message || "Failed to update", "error"); }
    } catch (error) { showMessage(error.response?.data?.message || "Error updating verification", "error"); }
    finally { setLoading(false); }
  };
  const cs = { backgroundColor: "#fff", borderRadius: "12px", padding: "24px", marginBottom: "24px", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" };
  const is = { padding: "10px 14px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px", width: "100%", boxSizing: "border-box" };
  const ths = { padding: "12px 16px", backgroundColor: "#f5f5f5", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "#555", borderBottom: "2px solid #eee" };
  const tds = { padding: "12px 16px", borderBottom: "1px solid #f0f0f0", fontSize: "14px" };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return api_urls.replace(/\/$/, '') + url;
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#333", marginBottom: "24px" }}>Name Verification Management</h2>

      {message.text && <div style={{ padding: "12px 16px", borderRadius: "8px", marginBottom: "16px", backgroundColor: message.type === "error" ? "#FFEBEE" : message.type === "success" ? "#E8F5E9" : "#E3F2FD", color: message.type === "error" ? "#C62828" : message.type === "success" ? "#2E7D32" : "#1565C0", fontSize: "14px", fontWeight: "500" }}>{message.text}</div>}

      <div style={cs}>
        <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#333", marginBottom: "16px" }}>Add Restricted Name</h3>
        <form onSubmit={handleAddName} style={{ display: "flex", gap: "12px", alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: "1", minWidth: "200px" }}>
            <label style={{ display: "block", fontSize: "13px", color: "#666", marginBottom: "6px", fontWeight: "500" }}>Name *</label>
            <input style={is} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Enter name to restrict" />
          </div>
          <div style={{ flex: "1", minWidth: "200px" }}>
            <label style={{ display: "block", fontSize: "13px", color: "#666", marginBottom: "6px", fontWeight: "500" }}>Reason</label>
            <input style={is} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for restriction" />
          </div>
          <button type="submit" style={{ padding: "10px 20px", backgroundColor: "#C62828", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "600" }} disabled={loading}>{loading ? "Adding..." : "+ Add Name"}</button>
        </form>
        <p style={{ fontSize: "12px", color: "#888", marginTop: "8px" }}>Note: Only users whose <strong>first name exactly matches</strong> will be flagged. Partial/substring matches are ignored.</p>
      </div>

      <div style={cs}>
        <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#333", marginBottom: "16px" }}>Search Users</h3>
        <div style={{ display: "flex", gap: "12px", alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ minWidth: "120px" }}>
            <label style={{ display: "block", fontSize: "13px", color: "#666", marginBottom: "6px", fontWeight: "500" }}>Search By</label>
            <select style={{ ...is, cursor: "pointer" }} value={searchType} onChange={(e) => { setSearchType(e.target.value); setSearchQuery(""); setSearchResults([]); }}>
              <option value="name">Name</option>
              <option value="phone">Phone Number</option>
            </select>
          </div>
          <div style={{ flex: "1" }}>
            <label style={{ display: "block", fontSize: "13px", color: "#666", marginBottom: "6px", fontWeight: "500" }}>{searchType === "phone" ? "Phone Number" : "User Name"}</label>
            <input style={is} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={searchType === "phone" ? "Enter phone number..." : "Search user by name..."} onKeyPress={(e) => e.key === "Enter" && handleSearchUsers()} />
          </div>
          <button onClick={handleSearchUsers} style={{ padding: "10px 20px", backgroundColor: "#2196F3", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "14px", fontWeight: "600" }} disabled={loading}>Search</button>
        </div>
        {searchResults.length > 0 && (
          <div style={{ marginTop: "16px" }}>
            <p style={{ fontSize: "13px", color: "#666", marginBottom: "8px" }}>Found {searchResults.length} user(s)</p>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={ths}>Photo</th>
                <th style={ths}>Name</th>
                <th style={ths}>Phone</th>
                <th style={ths}>Gender</th>
                <th style={ths}>Age</th>
                <th style={ths}>City</th>
                <th style={ths}>Status</th>
                <th style={ths}>Note</th>
                <th style={ths}>Actions</th>
              </tr></thead>
              <tbody>{searchResults.map((user, idx) => (
                <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                  <td style={tds}>
                    {user.profileImageUrl ? (
                      <img src={getImageUrl(user.profileImageUrl)} alt="" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: "2px solid #eee" }} onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#e0e0e0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", color: "#999" }}>?</div>
                    )}
                  </td>
                  <td style={{ ...tds, fontWeight: "600" }}>{user.firstName} {user.lastName}</td>
                  <td style={tds}>{user.phone || user.mobile || "-"}</td>
                  <td style={tds}>{user.gender || "-"}</td>
                  <td style={tds}>{user.age || "-"}</td>
                  <td style={tds}>{user.city || "-"}</td>
                  <td style={tds}><span style={{ display: "inline-block", padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: "600", backgroundColor: user.isVerified ? "#E8F5E9" : "#FFF3E0", color: user.isVerified ? "#2E7D32" : "#E65100" }}>{user.isVerified ? "Verified" : "Not Verified"}</span></td>
                  <td style={{ ...tds, fontSize: "12px", color: "#888", maxWidth: "150px" }}>{user.verificationNote || "-"}</td>
                  <td style={tds}><button onClick={() => handleToggleVerification(user._id, !user.isVerified, idx)} style={{ padding: "6px 14px", backgroundColor: user.isVerified ? "#EF5350" : "#4CAF50", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }} disabled={loading}>{user.isVerified ? "Unverify" : "Verify"}</button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>

      <div style={cs}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ fontSize: "18px", fontWeight: "600", color: "#333", margin: 0 }}>Restricted Names ({restrictedNames.length})</h3>
          <button onClick={fetchRestrictedNames} style={{ padding: "8px 16px", backgroundColor: "#2196F3", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>Refresh</button>
        </div>
        {loading && <p style={{ color: "#999" }}>Loading...</p>}
        {!loading && restrictedNames.length === 0 && <p style={{ color: "#999", textAlign: "center", padding: "24px" }}>No restricted names added yet</p>}
        {restrictedNames.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "16px" }}>
            <thead><tr><th style={ths}>#</th><th style={ths}>Name</th><th style={ths}>Reason</th><th style={ths}>Added On</th><th style={ths}>Action</th></tr></thead>
            <tbody>{restrictedNames.map((item, idx) => (
              <tr key={item._id || idx}><td style={tds}>{idx + 1}</td><td style={{ ...tds, fontWeight: "600", color: "#C62828" }}>{item.name}</td><td style={tds}>{item.reason || "-"}</td><td style={tds}>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"}</td><td style={tds}><button onClick={() => handleDeleteName(item._id)} style={{ padding: "6px 14px", backgroundColor: "#EF5350", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}>Remove</button></td></tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default NameVerification;
